
import { useAmplitude } from '@/hooks/useAmplitude';

interface SearchCompletedProperties {
  CheckinDate: string;
  CheckoutDate: string;
  Guests: string;
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface PropertyClickedProperties {
  PropertyName: string;
  PropertyShortName: string;
  PropertyRating: number;
  PropertyGuests: number;
  PropertyPrice: number;
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface WhatsAppClickedProperties {
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface SearchResultDisplayedProperties {
  NumberOfSearchResults: string;
  [key: string]: unknown;
}

interface LanguageChangedProperties {
  LanguageSelected: string;
  [key: string]: unknown;
}

interface FAQsClickedProperties {
  FAQ: string;
  [key: string]: unknown;
}

interface PropertyOwnerMoreInfoProperties {
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface PropertyDetailViewedProperties {
  PropertyName: string;
  PropertyShortName: string;
  PropertyRating: number;
  PropertyGuests: number;
  PropertyPrice: number;
  TotalNumberOfReviews: string;
  TotalNumberOfPhotos: string;
  HostName: string;
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface BookingEventProperties {
  CheckinDate: string;
  CheckoutDate: string;
  Guests: string;
  PropertyName: string;
  PropertyShortName: string;
  PropertyRating: number;
  PropertyGuests: number;
  PropertyPrice: number;
  TotalNumberOfReviews: string;
  TotalNumberOfPhotos: string;
  HostName: string;
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface BookingRequestedProperties {
  CheckinDate: string;
  CheckoutDate: string;
  Guests: string;
  PropertyName: string;
  PropertyShortName: string;
  PropertyRating: number;
  PropertyGuests: number;
  TotalCost: number;
  TotalNumberOfReviews: string;
  TotalNumberOfPhotos: string;
  HostName: string;
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

interface BookingModalClosedProperties {
  CheckinDate: string;
  CheckoutDate: string;
  Guests: string;
  PropertyName: string;
  PropertyShortName: string;
  PropertyRating: number;
  PropertyGuests: number;
  TotalCost: number;
  TotalNumberOfReviews: string;
  TotalNumberOfPhotos: string;
  HostName: string;
  PagePath: string;
  PageTitle: string;
  [key: string]: unknown;
}

export const useAmplitudeEvents = () => {
  const { trackEvent } = useAmplitude();

  const trackSearchCompleted = (
    checkinDate?: Date,
    checkoutDate?: Date,
    guests?: string
  ) => {
    const properties: SearchCompletedProperties = {
      CheckinDate: checkinDate ? checkinDate.toISOString().split('T')[0] : '',
      CheckoutDate: checkoutDate ? checkoutDate.toISOString().split('T')[0] : '',
      Guests: guests || '',
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Search Completed', properties);
  };

  const trackPropertyClicked = (
    propertyName: string,
    propertyShortName: string,
    propertyRating: number,
    propertyGuests: number,
    propertyPrice: number
  ) => {
    const properties: PropertyClickedProperties = {
      PropertyName: propertyName,
      PropertyShortName: propertyShortName,
      PropertyRating: propertyRating,
      PropertyGuests: propertyGuests,
      PropertyPrice: propertyPrice,
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Property Clicked', properties);
  };

  const trackWhatsAppClicked = () => {
    const properties: WhatsAppClickedProperties = {
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('WhatsApp Clicked', properties);
  };

  const trackSearchResultDisplayed = (numberOfResults: number) => {
    const properties: SearchResultDisplayedProperties = {
      NumberOfSearchResults: numberOfResults > 0 ? numberOfResults.toString() : ''
    };

    trackEvent('Search Result Displayed', properties);
  };

  const trackLanguageChanged = (language: string) => {
    const properties: LanguageChangedProperties = {
      LanguageSelected: language === 'es' ? 'Español' : 'Inglés'
    };

    trackEvent('Language Changed', properties);
  };

  const trackFAQsClicked = (faqTitle: string) => {
    const properties: FAQsClickedProperties = {
      FAQ: faqTitle
    };

    trackEvent('FAQs Clicked', properties);
  };

  const trackPropertyOwnerMoreInfo = () => {
    const properties: PropertyOwnerMoreInfoProperties = {
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Property Owner More Info', properties);
    console.log('📊 Property Owner More Info event tracked:', properties);
  };

  const trackPropertyDetailViewed = (
    propertyName: string,
    propertyShortName: string,
    propertyRating: number,
    propertyGuests: number,
    propertyPrice: number,
    totalReviews: number,
    totalPhotos: number,
    hostName: string
  ) => {
    const properties: PropertyDetailViewedProperties = {
      PropertyName: propertyName,
      PropertyShortName: propertyShortName,
      PropertyRating: propertyRating,
      PropertyGuests: propertyGuests,
      PropertyPrice: propertyPrice,
      TotalNumberOfReviews: totalReviews > 0 ? totalReviews.toString() : '',
      TotalNumberOfPhotos: totalPhotos > 0 ? totalPhotos.toString() : '',
      HostName: hostName,
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Property Detail Viewed', properties);
  };

  const trackRequestReservation = (
    checkinDate?: Date,
    checkoutDate?: Date,
    guests?: string,
    propertyName?: string,
    propertyShortName?: string,
    propertyRating?: number,
    propertyGuests?: number,
    propertyPrice?: number,
    totalReviews?: number,
    totalPhotos?: number,
    hostName?: string
  ) => {
    const properties: BookingEventProperties = {
      CheckinDate: checkinDate ? checkinDate.toISOString().split('T')[0] : '',
      CheckoutDate: checkoutDate ? checkoutDate.toISOString().split('T')[0] : '',
      Guests: guests || '',
      PropertyName: propertyName || '',
      PropertyShortName: propertyShortName || '',
      PropertyRating: propertyRating || 0,
      PropertyGuests: propertyGuests || 0,
      PropertyPrice: propertyPrice || 0,
      TotalNumberOfReviews: totalReviews ? totalReviews.toString() : '',
      TotalNumberOfPhotos: totalPhotos ? totalPhotos.toString() : '',
      HostName: hostName || '',
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Request Reservation', properties);
  };

  const trackAirbnbButtonClicked = (
    checkinDate?: Date,
    checkoutDate?: Date,
    guests?: string,
    propertyName?: string,
    propertyShortName?: string,
    propertyRating?: number,
    propertyGuests?: number,
    propertyPrice?: number,
    totalReviews?: number,
    totalPhotos?: number,
    hostName?: string
  ) => {
    const properties: BookingEventProperties = {
      CheckinDate: checkinDate ? checkinDate.toISOString().split('T')[0] : '',
      CheckoutDate: checkoutDate ? checkoutDate.toISOString().split('T')[0] : '',
      Guests: guests || '',
      PropertyName: propertyName || '',
      PropertyShortName: propertyShortName || '',
      PropertyRating: propertyRating || 0,
      PropertyGuests: propertyGuests || 0,
      PropertyPrice: propertyPrice || 0,
      TotalNumberOfReviews: totalReviews ? totalReviews.toString() : '',
      TotalNumberOfPhotos: totalPhotos ? totalPhotos.toString() : '',
      HostName: hostName || '',
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Airbnb Button Clicked', properties);
  };

  const trackWhatsAppBookingCardClicked = (
    checkinDate?: Date,
    checkoutDate?: Date,
    guests?: string,
    propertyName?: string,
    propertyShortName?: string,
    propertyRating?: number,
    propertyGuests?: number,
    propertyPrice?: number,
    totalReviews?: number,
    totalPhotos?: number,
    hostName?: string
  ) => {
    const properties: BookingEventProperties = {
      CheckinDate: checkinDate ? checkinDate.toISOString().split('T')[0] : '',
      CheckoutDate: checkoutDate ? checkoutDate.toISOString().split('T')[0] : '',
      Guests: guests || '',
      PropertyName: propertyName || '',
      PropertyShortName: propertyShortName || '',
      PropertyRating: propertyRating || 0,
      PropertyGuests: propertyGuests || 0,
      PropertyPrice: propertyPrice || 0,
      TotalNumberOfReviews: totalReviews ? totalReviews.toString() : '',
      TotalNumberOfPhotos: totalPhotos ? totalPhotos.toString() : '',
      HostName: hostName || '',
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('WhatsApp Booking Card Clicked', properties);
  };

  const trackWhatsAppBookingRequest = () => {
    const properties: WhatsAppClickedProperties = {
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('WhatsApp Booking Request', properties);
  };

  const trackBookingRequested = (
    checkinDate?: Date,
    checkoutDate?: Date,
    guests?: string,
    propertyName?: string,
    propertyShortName?: string,
    propertyRating?: number,
    propertyGuests?: number,
    totalCost?: number,
    totalReviews?: number,
    totalPhotos?: number,
    hostName?: string
  ) => {
    const properties: BookingRequestedProperties = {
      CheckinDate: checkinDate ? checkinDate.toISOString().split('T')[0] : '',
      CheckoutDate: checkoutDate ? checkoutDate.toISOString().split('T')[0] : '',
      Guests: guests || '',
      PropertyName: propertyName || '',
      PropertyShortName: propertyShortName || '',
      PropertyRating: propertyRating || 0,
      PropertyGuests: propertyGuests || 0,
      TotalCost: totalCost || 0,
      TotalNumberOfReviews: totalReviews ? totalReviews.toString() : '',
      TotalNumberOfPhotos: totalPhotos ? totalPhotos.toString() : '',
      HostName: hostName || '',
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Booking Requested', properties);
    console.log('📊 Booking Requested event tracked:', properties);
  };

  const trackBookingModalClosed = (
    checkinDate?: Date,
    checkoutDate?: Date,
    guests?: string,
    propertyName?: string,
    propertyShortName?: string,
    propertyRating?: number,
    propertyGuests?: number,
    totalCost?: number,
    totalReviews?: number,
    totalPhotos?: number,
    hostName?: string
  ) => {
    const properties: BookingModalClosedProperties = {
      CheckinDate: checkinDate ? checkinDate.toISOString().split('T')[0] : '',
      CheckoutDate: checkoutDate ? checkoutDate.toISOString().split('T')[0] : '',
      Guests: guests || '',
      PropertyName: propertyName || '',
      PropertyShortName: propertyShortName || '',
      PropertyRating: propertyRating || 0,
      PropertyGuests: propertyGuests || 0,
      TotalCost: totalCost || 0,
      TotalNumberOfReviews: totalReviews ? totalReviews.toString() : '',
      TotalNumberOfPhotos: totalPhotos ? totalPhotos.toString() : '',
      HostName: hostName || '',
      PagePath: window.location.pathname,
      PageTitle: document.title
    };

    trackEvent('Booking Modal Closed', properties);
    console.log('📊 Booking Modal Closed event tracked:', properties);
  };

  return {
    trackSearchCompleted,
    trackPropertyClicked,
    trackWhatsAppClicked,
    trackSearchResultDisplayed,
    trackLanguageChanged,
    trackFAQsClicked,
    trackPropertyOwnerMoreInfo,
    trackPropertyDetailViewed,
    trackRequestReservation,
    trackAirbnbButtonClicked,
    trackWhatsAppBookingCardClicked,
    trackWhatsAppBookingRequest,
    trackBookingRequested,
    trackBookingModalClosed
  };
};
